import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ChatMessage,
  GameEvent,
  MatchEnded,
  MatchSnapshot,
  PresenceUpdate,
} from '@bn/shared';
import { CHAT_MAX_LENGTH } from '@bn/shared';
import { ask, useSocket } from '@/lib/socket';
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui';
import { getGameUi } from '@/games/registry';

export function MatchPage() {
  const { matchId = '' } = useParams();
  const { socket, connected } = useSocket();
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState<MatchSnapshot | null>(null);
  const [ended, setEnded] = useState<MatchEnded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clockOffset, setClockOffset] = useState(0);

  const sync = useCallback(async () => {
    if (!socket) return;
    try {
      const data = await ask<MatchSnapshot>(socket, 'match:sync', { matchId });
      setSnapshot(data);
      setClockOffset(data.serverTime - Date.now());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Partie inaccessible.');
    }
  }, [socket, matchId]);

  useEffect(() => {
    if (!socket || !connected) return;
    void sync();
  }, [socket, connected, sync]);

  useEffect(() => {
    if (!socket) return;

    const onState = (payload: MatchSnapshot) => {
      if (payload.matchId !== matchId) return;
      setSnapshot(payload);
      setClockOffset(payload.serverTime - Date.now());
    };
    const onEvent = (payload: { matchId: string; events: GameEvent[] }) => {
      if (payload.matchId !== matchId) return;
      setSnapshot((current) =>
        current ? { ...current, log: [...current.log, ...payload.events] } : current,
      );
    };
    const onEnded = (payload: MatchEnded) => {
      if (payload.matchId !== matchId) return;
      setEnded(payload);
    };
    const onChat = (message: ChatMessage) => {
      if (message.matchId !== matchId) return;
      setSnapshot((current) =>
        current ? { ...current, chat: [...current.chat, message] } : current,
      );
    };
    const onPresence = (payload: PresenceUpdate) => {
      if (payload.matchId !== matchId) return;
      setSnapshot((current) =>
        current && current.opponent.id === payload.userId
          ? { ...current, opponentOnline: payload.online }
          : current,
      );
    };

    socket.on('match:state', onState);
    socket.on('match:event', onEvent);
    socket.on('match:ended', onEnded);
    socket.on('chat:message', onChat);
    socket.on('presence', onPresence);

    return () => {
      socket.off('match:state', onState);
      socket.off('match:event', onEvent);
      socket.off('match:ended', onEnded);
      socket.off('chat:message', onChat);
      socket.off('presence', onPresence);
    };
  }, [socket, matchId]);

  const sendAction = useCallback(
    async (action: unknown) => {
      if (!socket) throw new Error('Non connecté au serveur.');
      await ask(socket, 'match:action', { matchId, action });
    },
    [socket, matchId],
  );

  const nameOf = useCallback(
    (id: string | undefined) => {
      if (!snapshot || !id) return 'Quelqu’un';
      if (id === snapshot.you.id) return snapshot.you.displayName;
      if (id === snapshot.opponent.id) return snapshot.opponent.displayName;
      return 'Quelqu’un';
    },
    [snapshot],
  );

  const ui = snapshot ? getGameUi(snapshot.gameId) : undefined;

  const journal = useMemo(() => {
    if (!snapshot || !ui) return [];
    return snapshot.log
      .map((event, index) => ({
        key: `${index}-${event.type}`,
        text: ui.describeEvent(event, nameOf),
        at: typeof event.at === 'string' ? event.at : null,
      }))
      .filter((line): line is { key: string; text: string; at: string | null } =>
        Boolean(line.text),
      )
      .reverse();
  }, [snapshot, ui, nameOf]);

  if (error) {
    return (
      <div className="space-y-4">
        <Alert>{error}</Alert>
        <Button variant="secondaire" onClick={() => navigate('/')}>
          Retour au salon
        </Button>
      </div>
    );
  }

  if (!snapshot || !ui) {
    return <Spinner label="Chargement de la partie…" />;
  }

  const { View } = ui;

  return (
    <div className="space-y-4">
      {!connected && <Alert type="info">Reconnexion au serveur…</Alert>}

      {ended && (
        <Card className="border-sky-500/40 bg-sky-950/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold text-sky-100">
                {ended.winnerId === null
                  ? 'Partie terminée sans vainqueur.'
                  : ended.winnerId === snapshot.you.id
                    ? 'Vous remportez la partie.'
                    : `${snapshot.opponent.displayName} remporte la partie.`}
              </p>
              <p className="text-sm text-slate-300">
                {ended.ratings.map((change) => {
                  const delta = change.after - change.before;
                  return (
                    <span key={change.userId} className="mr-4">
                      {nameOf(change.userId)} : {change.after}{' '}
                      <span className={delta >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                        ({delta >= 0 ? '+' : ''}
                        {delta})
                      </span>
                    </span>
                  );
                })}
              </p>
            </div>
            <Button onClick={() => navigate('/')}>Retour au salon</Button>
          </div>
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <View
          view={snapshot.view}
          you={snapshot.you}
          opponent={snapshot.opponent}
          opponentOnline={snapshot.opponentOnline}
          clockOffset={clockOffset}
          onAction={sendAction}
        />

        <div className="space-y-4">
          <ChatPanel matchId={matchId} messages={snapshot.chat} youId={snapshot.you.id} />

          <Card title="Journal">
            <ol className="max-h-72 space-y-1.5 overflow-y-auto text-sm text-slate-300">
              {journal.length === 0 && (
                <li className="text-slate-500">Aucun évènement pour l’instant.</li>
              )}
              {journal.map((line) => (
                <li key={line.key} className="border-b border-slate-800/60 pb-1">
                  {line.text}
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ChatPanel({
  matchId,
  messages,
  youId,
}: {
  matchId: string;
  messages: ChatMessage[];
  youId: string;
}) {
  const { socket } = useSocket();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = body.trim();
    if (!text || !socket) return;
    setError(null);
    try {
      await ask(socket, 'chat:send', { matchId, body: text });
      setBody('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Envoi impossible.');
    }
  }

  return (
    <Card title="Discussion">
      <div ref={listRef} className="mb-3 max-h-64 space-y-2 overflow-y-auto pr-1">
        {messages.length === 0 && (
          <p className="text-sm text-slate-500">Aucun message.</p>
        )}
        {messages.map((message) => (
          <div key={message.id} className="text-sm">
            <span
              className={
                message.userId === youId
                  ? 'font-semibold text-sky-300'
                  : 'font-semibold text-slate-200'
              }
            >
              {message.username}
            </span>
            <span className="ml-2 text-slate-300">{message.body}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-2">
          <Alert>{error}</Alert>
        </div>
      )}

      <form onSubmit={(e) => void submit(e)} className="flex gap-2">
        <input
          value={body}
          maxLength={CHAT_MAX_LENGTH}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Votre message…"
          className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 outline-none focus:border-sky-400"
        />
        <Button type="submit" disabled={!body.trim()}>
          <span aria-hidden>➤</span>
          <span className="sr-only">Envoyer</span>
        </Button>
      </form>
      <p className="mt-2 text-right text-xs text-slate-500">
        <Badge>{messages.length}</Badge>
      </p>
    </Card>
  );
}
